import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface SunVideoProps {
  width?: number | string;
  height?: number | string;
}

const SunFallbackCanvas: React.FC<{ size: number }> = ({ size }) => {
  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    
    const pulse = Math.sin(frameCount * 0.05) * 5;

    ctx.save();
    const gradient = ctx.createRadialGradient(centerX, centerY, 10, centerX, centerY, 80 + pulse);
    gradient.addColorStop(0, '#FFFFFF');
    gradient.addColorStop(0.2, '#FDE047');
    gradient.addColorStop(0.6, '#EA580C');
    gradient.addColorStop(1, 'rgba(234, 88, 12, 0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 80 + pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return <canvas ref={canvasRef} style={{ borderRadius: '12px' }} />;
};

export const SunVideo: React.FC<SunVideoProps> = ({ width = '100%', height = 400 }) => {
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Auto-play workaround for some strict browsers
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked or video failed to load immediately
        setHasError(true);
      });
    }
  }, []);

  return (
    <div style={{ width, height, display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0F172A', borderRadius: '12px', overflow: 'hidden' }}>
      {!hasError ? (
        <video
          ref={videoRef}
          src="https://www.nasa.gov/wp-content/uploads/2023/07/sdo-sunspot.mp4"
          autoPlay
          muted
          loop
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setHasError(true)}
        />
      ) : (
        <SunFallbackCanvas size={Number(height)} />
      )}
    </div>
  );
};
